import React from 'react';

import { Form, Input, Select, DatePicker } from 'antd';
import { FormInstance, Rule } from 'antd/lib/form';
import { config } from 'config/config';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { isConPlatform, minAgeDate, passwordRegex } from 'utils';
import { PlatformEnum } from 'src/shared/enums';
import { AutocompleteAttributeTypes } from '../interfaces';

const {
  REGISTER: { MIN_REGISTER_DATE },
} = config;
const { Option } = Select;

export const CustomInputText = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  onChange?: (e: any) => void,
  initialValue?: any,
  maxLength?: number,
  required = true,
  rules: object[] = [],
  value?: any,
  autocomplete?: AutocompleteAttributeTypes,
  autoFocus = false,
  disabled?: boolean,
  switchProps?: {
    inputId: string;
    onKeyDown: (ev: any) => void;
  },
  customTriggers = null,
) => {
  let requiredMessage: string;
  const { formatMessage } = useIntl();

  let validateTrigger = 'onChange';

  switch (name) {
    case 'name':
    case 'given-name':
    case 'last-name':
    case 'phone':
    case 'directionStreet':
    case 'city':
    case 'surname':
    case 'address':
    case 'zipCode':
    case 'rut':
    case 'streetNumber':
    case 'idNumber':
    case 'district':
      validateTrigger = 'onChange';
      requiredMessage = formatMessage({ id: `form.field.${name}-is-required` });
      break;
    case 'telephone':
      validateTrigger = 'onBlur';
      requiredMessage = formatMessage({ id: `form.field.${name}-is-required` });
      break;
    default:
      break;
  }

  if (customTriggers) validateTrigger = customTriggers;

  const inputRules: object[] = rules ?? [];
  inputRules.push(
    required
      ? requiredMessage
        ? { required: true, message: requiredMessage }
        : { required: true }
      : { required: false },
  );

  let inputOptions: any = {
    disabled: disabled,
    autoComplete: autocomplete,
    autoFocus,
    defaultValue: form?.getFieldValue(name) || initialValue,
    formNoValidate: true,
    maxLength: maxLength ?? 255,
    placeholder: ' ',
    required,
  };

  if (onChange) inputOptions = { ...inputOptions, onChange };
  if (value || value === '') inputOptions.value = value;
  if (switchProps) {
    inputOptions = {
      ...inputOptions,
      ['data-index']: switchProps.inputId,
      onKeyDown: switchProps.onKeyDown,
    };
  }

  return (
    <Form.Item
      name={name}
      rules={inputRules}
      validateTrigger={validateTrigger}
      extra={
        !required ? formatMessage({ id: 'form.field.optional-required' }) : null
      }>
      <div className="custom-text-field" key={name}>
        <Input {...inputOptions} />
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
      </div>
    </Form.Item>
  );
};

export const CustomInputTextArea = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  onChange?: (e: any) => void,
  initialValue?: any,
  maxLength?: number,
  required = true,
  rules: object[] = [],
  value?: any,
  autocomplete?: string,
  autoFocus = false,
  switchProps?: {
    inputId: string;
    onKeyDown: (ev: any) => void;
  },
  customTriggers = null,
) => {
  let requiredMessage: string;
  const { formatMessage } = useIntl();

  let validateTrigger = 'onChange';

  switch (name) {
    case 'name':
    case 'given-name':
    case 'last-name':
    case 'phone':
    case 'directionStreet':
    case 'city':
    case 'surname':
    case 'address':
    case 'zipCode':
    case 'rut':
      validateTrigger = 'onChange';
      requiredMessage = formatMessage({ id: `form.field.${name}-is-required` });
      break;
    case 'telephone':
      validateTrigger = 'onBlur';
      requiredMessage = formatMessage({ id: `form.field.${name}-is-required` });
      break;
    default:
      break;
  }

  if (customTriggers) validateTrigger = customTriggers;

  const inputRules: object[] = rules ?? [];
  inputRules.push(
    required
      ? requiredMessage
        ? { required: true, message: requiredMessage }
        : { required: true }
      : { required: false },
  );

  let inputOptions: any = {
    autoComplete: autocomplete,
    autoFocus,
    defaultValue: form?.getFieldValue(name) || initialValue,
    formNoValidate: true,
    maxLength: maxLength ?? 1000,
    placeholder: ' ',
    required,
  };

  if (onChange) inputOptions = { ...inputOptions, onChange };
  if (value || value === '') inputOptions.value = value;
  if (switchProps) {
    inputOptions = {
      ...inputOptions,
      ['data-index']: switchProps.inputId,
      onKeyDown: switchProps.onKeyDown,
    };
  }

  return (
    <Form.Item
      name={name}
      rules={inputRules}
      validateTrigger={validateTrigger}
      extra={formatMessage({ id: 'form.field.optional-required' })}>
      <div className="custom-text-field" key={name}>
        <textarea col="200em" row="5em" {...inputOptions} />
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
      </div>
    </Form.Item>
  );
};

export const CustomInputDate = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  onChange: (e: any) => void,
  onInput: (e: any) => void,
  minAge?: number,
) => {
  const { formatMessage } = useIntl();

  const inputRules: any = [
    {
      required: true,
      message: formatMessage({ id: `form.field.${name}-is-required` }),
    },
  ];

  const inputOptions: any = {
    required: true,
    formNoValidate: true,
    placeholder: ' ',
  };

  if (minAge) {
    const minDate = minAgeDate(minAge);

    inputRules.push({
      validator: (_: any, value: any) => {
        const birthday = Date.parse(value);
        const min = Date.parse(minDate);

        const isValidDate = birthday <= min;

        return isValidDate
          ? Promise.resolve()
          : Promise.reject(
              new Error(formatMessage({ id: 'register.error.wrong-date' })),
            );
      },
    });
  }

  return (
    <Form.Item name={name} rules={inputRules}>
      <div className="custom-text-field" key={name}>
        <Input
          type="date"
          max="9999-12-31"
          {...inputOptions}
          onChange={onChange}
          onInput={onInput}
        />
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
      </div>
    </Form.Item>
  );
};

export const CustomInputDatePicker = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  onChange?: (e: any) => void,
  initialValue?: any,
  minAge?: number,
) => {
  const { formatMessage } = useIntl();

  const dateFormatList = ['DD/MM/YYYY'];

  const rules: any = [
    {
      required: true,
      message: formatMessage({ id: `form.field.${name}-is-required` }),
    },
  ];

  let inputOptions: any = {
    required: true,
    formNoValidate: true,
    placeholder: 'dd/mm/yyyy',
    defaultValue: form?.getFieldValue(name) || initialValue,
  };

  if (minAge) {
    const disabledDate = (current: moment.Moment) => {
      if (!current) return true;
      const minMoment = moment().subtract(minAge, 'years');
      const maxMoment = moment(MIN_REGISTER_DATE, 'YYYY/MM/DD');
      return !current.isBetween(maxMoment, minMoment);
    };

    inputOptions = { ...inputOptions, disabledDate };
  }

  if (onChange) {
    inputOptions = { ...inputOptions, onChange };
  }

  return (
    <Form.Item name={name} rules={rules}>
      <div className="custom-text-field" key={name}>
        <DatePicker
          bordered={false}
          format={dateFormatList}
          {...inputOptions}
        />
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
      </div>
    </Form.Item>
  );
};

export const CustomOnlyNumber = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  onChange?: (e: any) => void,
  initialValue?: any,
  maxLength?: number,
  required = true,
  rules: object[] = [],
  value?: any,
  autocomplete?: AutocompleteAttributeTypes,
) => {
  let requiredMessage: string;
  const { formatMessage } = useIntl();
  const zipCode: string = 'zipCode';
  const { PLATFORM } = config.APP;

  switch (name) {
    case 'telephone':
    case 'phone':
    case 'directionNumber':
    case 'zipCode':
    case 'rut':
    case 'streetNumber':
      requiredMessage = formatMessage({ id: `form.field.${name}-is-required` });
      break;
    default:
      break;
  }

  const inputRules: object[] = rules ?? [];
  inputRules.push(
    required
      ? requiredMessage
        ? { required: true, message: requiredMessage }
        : { required: true }
      : { required: false },
  );

  const handleOnChangesZipcodeSpain = (e) => {
    const { value } = e.target;
    const reg = /^[\d]{0,5}$/;

    if ((!isNaN(value) && reg.test(value)) || value === '') {
      onChange(value);
    } else {
      e.stopPropagation();
    }
  };

  const handleOnChangesTelephoneSpain = (e) => {
    const { value } = e.target;
    const reg = /^(?:[679]{1}[0-9]{0,8})$/;

    if ((!isNaN(value) && reg.test(value)) || value === '') {
      onChange(value);
    } else {
      e.stopPropagation();
    }
  };

  const handleOnChanges = (e) => {
    const { value } = e.target;
    const reg = /^-?\d*(\.\d*)?$/;

    if ((!isNaN(value) && reg.test(value)) || value === '' || value === '-') {
      onChange(value);
    }
  };

  let inputOptions: any = {
    required: required,
    formNoValidate: true,
    placeholder: ' ',
    defaultValue: form?.getFieldValue(name) || initialValue,
    maxLength: maxLength ?? 255,
    autoComplete: autocomplete,
    value: form?.getFieldValue(name),
  };

  if (onChange) inputOptions = { ...inputOptions, onChange };
  if (value || value === '') inputOptions.value = value;
  return (
    <Form.Item name={name} rules={inputRules}>
      <div className="custom-text-field" key={name}>
        <Input
          {...inputOptions}
          onChange={
            PLATFORM === PlatformEnum.CON_SPAIN && name === zipCode
              ? handleOnChangesZipcodeSpain
              : PLATFORM === PlatformEnum.CON_SPAIN && name === 'phone'
              ? handleOnChangesTelephoneSpain
              : handleOnChanges
          }
        />
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
      </div>
    </Form.Item>
  );
};

export const CustomInputNumber = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  onChange?: (e: any) => void,
  initialValue?: any,
  maxLength?: number,
) => {
  const { formatMessage } = useIntl();

  let requiredMessage: string;

  switch (name) {
    case 'telephone':
    case 'directionNumber':
    case 'zipCode':
    case 'rut':
    case 'idNumber':
      requiredMessage = formatMessage({ id: `form.field.${name}-is-required` });
      break;
    default:
      break;
  }

  const rules = requiredMessage
    ? [{ required: true, message: requiredMessage }]
    : [{ required: true }];

  let inputOptions: any = {
    min: 0,
    required: true,
    formNoValidate: true,
    placeholder: ' ',
    defaultValue: form?.getFieldValue(name) || initialValue,
    maxLength: maxLength ?? 10,
  };

  if (onChange) inputOptions = { ...inputOptions, onChange };

  return (
    <Form.Item name={name} rules={rules}>
      <div className="custom-text-field" key={name}>
        <Input {...inputOptions} />
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
      </div>
    </Form.Item>
  );
};

export const CustomInputTextIconAction = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  iconName: string,
  onChange?: (e: any) => void,
  initialValue?: any,
) => {
  return (
    <Form.Item name={name} rules={[{ required: true }]}>
      <div className="custom-text-field" key={name}>
        <Input
          required
          formNoValidate
          placeholder={' '}
          defaultValue={form?.getFieldValue(name) || initialValue}
          onChange={onChange}
        />
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
        <a className={`icon ${iconName}`} onClick={onChange} />
      </div>
    </Form.Item>
  );
};

export const CustomInputTextOptional = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  onChange?: (e: any) => void,
  initialValue?: any,
  maxLength?: number,
) => {
  let inputOptions: any = {
    placeholder: ' ',
    formNoValidate: true,
    defaultValue: form?.getFieldValue(name) || initialValue,
    maxLength: maxLength ?? 100,
  };

  if (onChange) inputOptions = { ...inputOptions, onChange };

  return (
    <Form.Item name={name} rules={[]}>
      <div className="custom-text-field" key={name}>
        <Input {...inputOptions} />
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
      </div>
    </Form.Item>
  );
};

export const CustomInputEmail = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  onChange?: (e: any) => void,
  initialValue?: any,
  maxLength?: number,
  options?: any,
) => {
  const rules: Rule[] = [
    { required: true, type: 'email', message: 'Email format is not valid' },
  ];
  if (name === 'emailRepeat')
    rules.push(({ getFieldValue }) => ({
      validator(rule, value) {
        if (!value || getFieldValue('email') === value) {
          return Promise.resolve();
        }
        return Promise.reject('Email does not match');
      },
    }));
  let inputOptions: any = {
    required: true,
    formNoValidate: true,
    autoComplete: 'none',
    placeholder: ' ',
    defaultValue: form?.getFieldValue(name) || initialValue,
    maxLength: maxLength ?? 80,
  };

  if (onChange) inputOptions = { ...inputOptions, onChange };
  if (options) inputOptions = { ...inputOptions, ...options };

  return (
    <Form.Item name={name} rules={rules}>
      <div className="custom-text-field" key={name}>
        <Input {...inputOptions} />
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
      </div>
    </Form.Item>
  );
};

export const CustomInputPassword = (
  form: FormInstance,
  name: string,
  placeHolder: string,
  onChange?: (e: any) => void,
  initialValue?: any,
  autoFocus = false,
  disabled?: boolean,
  switchProps?: {
    inputId: string;
    onKeyDown: (ev: any) => void;
  },
  rules: any[] = [],
) => {
  const { formatMessage } = useIntl();
  const value = form?.getFieldValue(name) || initialValue;

  const extraProps = switchProps
    ? {
        ['data-index']: switchProps.inputId,
        onKeyDown: switchProps.onKeyDown,
      }
    : {};

  return (
    <>
      {isConPlatform ? (
        <Form.Item
          className="custom-text-field__password"
          name={name}
          rules={rules}>
          <div
            className={`custom-text-field ${
              !!value ? 'show-place-holder' : ''
            }`}
            key={name}>
            <Input.Password
              required
              formNoValidate
              autoComplete="new-password"
              defaultValue={value}
              onChange={onChange}
              autoFocus={autoFocus}
              disabled={disabled}
              {...extraProps}
            />
            <label id={name}>
              <FormattedMessage id={placeHolder} />
            </label>
          </div>
        </Form.Item>
      ) : (
        <Form.Item
          name={name}
          rules={[
            {
              required: true,
              whitespace: true,
              type: 'string',
              message: formatMessage({
                id: 'page.password-required',
              }),
            },
            {
              pattern: passwordRegex,
              message: formatMessage({
                id: 'form.message.password-validate.length',
              }),
            },
          ]}>
          <div
            className={`custom-text-field ${
              !!value ? 'show-place-holder' : ''
            }`}
            key={name}>
            <Input.Password
              required
              formNoValidate
              autoComplete="none"
              defaultValue={value}
              onChange={onChange}
              disabled={disabled}
            />
            <label id={name}>
              <FormattedMessage id={placeHolder} />
            </label>
          </div>
        </Form.Item>
      )}
    </>
  );
};

const renderLabel = (value: any, name: string, placeHolder: string) => {
  return (
    <>
      {value != undefined && (
        <label id={name}>
          <FormattedMessage id={placeHolder} />
        </label>
      )}
    </>
  );
};

export const CustomSelector = (
  form: FormInstance,
  name: string,
  options: any[],
  placeHolder: string,
  key = 'id',
  onChange?: (e: any) => void,
  applyTranslation?: boolean,
  optionLabel = 'name',
  required = true,
  rules: object[] = [],
  useRequiredMessage: boolean = true,
  disabled: boolean = false,
) => {
  const value = form?.getFieldValue(name);
  const { formatMessage } = useIntl();

  const handleChange = (_value: number) => {
    const _form = form.getFieldsValue();
    _form[name] = _value;

    form.setFieldsValue(_form);
    if (onChange != null) onChange(_value);
  };

  let requiredMessage: string;

  switch (name) {
    case 'idSpecialty':
    case 'idSalesforce':
    case 'idCenter':
    case 'state':
    case 'regionName':
    case 'district':
    case 'city':
      requiredMessage = formatMessage({ id: `form.field.${name}-is-required` });
      break;
  }

  const inputRules: object[] = rules ?? [];
  inputRules.push(
    required
      ? requiredMessage
        ? { required: true, message: requiredMessage }
        : { required: true }
      : { required: false },
  );

  return (
    <Form.Item name={name} rules={inputRules}>
      <div className="custom-text-field" key={name}>
        <Select
          disabled={disabled}
          defaultValue={value == -1 ? null : value}
          className={`${
            value == undefined ? 'no-label' : 'show-place-holder'
          } fs-mask`}
          onChange={handleChange}
          placeholder={
            useRequiredMessage
              ? requiredMessage
              : formatMessage({ id: placeHolder })
          }
          filterOption={(input, option) => {
            return (
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            );
          }}>
          {options?.map((value, index) => (
            <Option key={index} value={value[key].toString()}>
              {applyTranslation
                ? formatMessage({ id: value[optionLabel] })
                : value[optionLabel]}
            </Option>
          ))}
        </Select>
        {renderLabel(value, name, placeHolder)}
      </div>
    </Form.Item>
  );
};
